import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService, ClientStats } from '../services/api';
import { Users, CheckCircle, Gem, Target, Stethoscope, CircleDollarSign, BarChart3 } from 'lucide-react';
interface Client {
  id: number;
  client_name: string;
  client_email: string;
  client_phone: string;
  total_appointments: number;
  completed_appointments: number;
  last_appointment: string;  // ← Este campo
  total_spent: number;
  status: string;  // ← Este campo
  picture?: string;
  first_visit?: string;
  favorite_service?: string;
}
// =====================================================
// COMPONENTE GENÉRICO CLIENTS - PARA SALUD Y BELLEZA
// =====================================================

interface ThemeConfig {
  gradient: string;
  bgGradient: string;
  primary: string;
  clientLabel: string;
  icons: {
    client: React.ReactNode;
    active: React.ReactNode;
    value: React.ReactNode;
    retention: React.ReactNode;
  };
}

interface ClientsProps {
  clients?: any[];
  loading?: boolean;
  onClientClick?: (client: any) => void;
}

const Clients: React.FC<ClientsProps> = ({ clients: externalClients, loading: externalLoading, onClientClick }) => {
  const navigate = useNavigate();
  
  // Estados principales
  const [clients, setClients] = useState<Client[]>([]);
  const [stats, setStats] = useState<ClientStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Estados del modal de detalle
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [clientDetail, setClientDetail] = useState<any>(null);

  // Obtener usuario actual
  const [user, setUser] = useState<any>(null);

  // Detectar tema basado en el tipo de profesional
  const getThemeConfig = (professionalType: 'salud' | 'belleza'): ThemeConfig => {
    if (professionalType === 'belleza') {
      return {
        gradient: 'from-pink-500 to-purple-500',
        bgGradient: 'from-pink-50 to-purple-50',
        primary: 'pink',
        clientLabel: 'Mis Clientas',
        icons: {
          client: <Users className="w-6 h-6" />,
          active: <CheckCircle className="w-6 h-6" />,
          value: <Gem className="w-6 h-6" />,
          retention: <Target className="w-6 h-6" />
        }
      };
    } else {
      return {
        gradient: 'from-blue-500 to-green-500',
        bgGradient: 'from-blue-50 to-green-50',
        primary: 'blue',
        clientLabel: 'Mis Pacientes',
        icons: {
          client: <Users className="w-6 h-6" />,
          active: <Stethoscope className="w-6 h-6" />,
          value: <CircleDollarSign className="w-6 h-6" />,
          retention: <BarChart3 className="w-6 h-6" />
        }
      };
    }
  };

  const [theme, setTheme] = useState<ThemeConfig>(getThemeConfig('belleza'));

  useEffect(() => {
    // Verificar usuario
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      setTheme(getThemeConfig(parsedUser.professional_type || 'belleza'));
    }
  }, []);

  useEffect(() => {
    if (user?.id) {
      loadClients();
    }
  }, [user?.id]);

  // Cargar clientes desde el backend
  const loadClients = async (search?: string) => {
    if (!user?.id) return;
    
    try {
      if (search !== undefined) {
        setSearchLoading(true);
      } else {
        setLoading(true);
      }
      setError(null);

      console.log(`Loading clients for professional: ${user.id}`);
      
      const response = await apiService.getClients(user.id, search);
      
      if (response.success) {
        const transformedClients: Client[] = response.clients.map((client: any) => ({
  ...client,
  last_appointment: client.last_appointment || client.last_visit || '',
  status: client.status || 'active'
}));
setClients(transformedClients);
        setStats(response.stats);
        console.log(`Clients loaded: ${response.clients.length}`);
      } else {
        throw new Error('Error en la respuesta del servidor');
      }
    } catch (err: any) {
      console.error('Error loading clients:', err);
      setError(apiService.getErrorMessage(err));
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  // Buscar clientes
  const handleSearch = async (searchValue: string) => {
    setSearchTerm(searchValue);
    
    // Debounce: esperar 500ms después de que el usuario deje de escribir
    setTimeout(() => {
      if (searchValue === searchTerm) {
        loadClients(searchValue.trim() || undefined);
      }
    }, 500);
  };

  // Ver detalle del cliente
const handleViewDetail = async (client: Client) => {
  setSelectedClient(client);
  setShowDetailModal(true);
  setDetailLoading(true);
  setClientDetail(null);

  try {
    console.log('Loading client detail:', client.client_name, 'Email:', client.client_email, 'Phone:', client.client_phone);

    const response = await apiService.getClientDetail(user.id, client.client_email || '', client.client_phone);

    if (response.success) {
      setClientDetail(response);
      console.log('Client detail loaded successfully');
    }
  } catch (err: any) {
    console.error('Error loading detail:', err);
    // Fallback a datos básicos si falla
    setClientDetail({
      client: {
        name: client.client_name || 'Sin nombre',
        email: client.client_email || 'No registrado',
        phone: client.client_phone || 'No registrado',
        totalAppointments: client.total_appointments || 0,
        completedAppointments: client.completed_appointments || 0,
        totalSpent: client.total_spent || 0,
        firstVisit: client.first_visit || 'N/A',
        last_appointment: client.last_appointment || 'N/A',
        favoriteServices: client.favorite_service ? [client.favorite_service] : []
      },
      appointments: []
    });
  } finally {
    setDetailLoading(false);
  }
};

  // Crear nueva cita para un cliente
  const handleNewAppointment = (client: Client) => {
    // Navegar al formulario de nueva cita con datos pre-rellenados
    navigate('/professional/new-appointment', { 
      state: { 
        prefilledClient: {
          name: client.client_name,
          email: client.client_email,
          phone: client.client_phone
        }
      }
    });
  };

  // ✅ NUEVA FUNCIÓN: Ver historia clínica del paciente
  const handleViewClinicalRecords = (client: Client) => {
    navigate('/professional/clinical-records', { 
      state: { 
        preselectedPatient: {
          name: client.client_name,
          email: client.client_email,
          phone: client.client_phone
        }
      }
    });
  };

  // Funciones de utilidad
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'recent': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Activo';
      case 'recent': return 'Reciente'; 
      case 'inactive': return 'Inactivo';
      default: return 'Desconocido';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

 const getInitials = (name: string) => {
  if (!name || typeof name !== 'string') {
    return '??';
  }
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

  // Filtrar clientes localmente también (para búsqueda inmediata)
  const filteredClients = searchTerm.trim()
    ? clients.filter(client =>
        (client.client_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.client_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.client_phone || '').includes(searchTerm)
      )
    : clients;

  // ✅ Determinar si es profesional de salud
  const isHealthProfessional = user?.professional_type === 'salud';

  if (!user) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${theme.bgGradient} flex items-center justify-center`}>
        <div className="text-center">
          <p className="text-red-600">Error: Usuario no encontrado</p>
          <button 
            onClick={() => navigate('/login')}
            className={`mt-4 bg-gradient-to-r ${theme.gradient} text-white px-4 py-2 rounded-lg hover:shadow-lg transition-shadow`}
          >
            Ir a Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.bgGradient}`}>
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">{theme.icons.client} {theme.clientLabel}</h1>
              <p className="text-gray-600">
                Gestiona tu base de {user.professional_type === 'belleza' ? 'clientas' : 'pacientes'}
                {stats && <span className="ml-2">• {stats.totalClients} registrados</span>}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => loadClients()}
                disabled={loading}
                className={`bg-gradient-to-r ${theme.gradient} text-white px-4 py-2 rounded-lg hover:shadow-lg transition-shadow disabled:opacity-50`}
              >
                Actualizar
              </button>
              <button 
                onClick={() => navigate(-1)} 
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                ← Volver
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Estadísticas */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total {user.professional_type === 'belleza' ? 'Clientas' : 'Pacientes'}</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalClients}</p>
                </div>
                <div className={`w-12 h-12 bg-${theme.primary}-100 rounded-lg flex items-center justify-center`}>
                  <span className="text-2xl">{theme.icons.client}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{user.professional_type === 'belleza' ? 'Clientas' : 'Pacientes'} Activos</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.activeClients}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">{theme.icons.active}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Valor Promedio</p>
                  <p className="text-2xl font-bold text-gray-900">${stats.avgClientValue}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">{theme.icons.value}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Retención</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.clientRetentionRate}%</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">{theme.icons.retention}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Buscador */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <input
              type="text"
              placeholder={`Buscar ${user.professional_type === 'belleza' ? 'clienta' : 'paciente'} por nombre o email...`}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-4 pr-10 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchLoading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
              </div>
            )}
          </div>
        </div>

        {/* Lista de Clientes */}
        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <p className="text-red-600 mb-4">Error: {error}</p>
            <button 
              onClick={() => loadClients()}
              className="bg-red-100 text-red-600 px-4 py-2 rounded-lg hover:bg-red-200"
            >
              Reintentar
            </button>
          </div>
        ) : loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">{theme.icons.client}</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? `No se encontraron ${user.professional_type === 'belleza' ? 'clientas' : 'pacientes'}` : `Aún no tienes ${user.professional_type === 'belleza' ? 'clientas' : 'pacientes'}`}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm 
                ? `No hay registros que coincidan con "${searchTerm}"`
                : `Cuando tengas tu primera cita, aparecerá aquí la información de tus ${user.professional_type === 'belleza' ? 'clientas' : 'pacientes'}`
              }
            </p>
            {!searchTerm && (
              <button
                onClick={() => navigate('/professional/new-appointment')}
                className={`bg-gradient-to-r ${theme.gradient} text-white px-6 py-2 rounded-lg hover:shadow-lg transition-shadow`}
              >
                Crear Primera Cita
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClients.map((client) => (
              <div key={client.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-12 h-12 bg-gradient-to-r ${theme.gradient} rounded-full flex items-center justify-center`}>
                      <span className="text-white font-semibold">
  {getInitials(client.client_name || 'Sin Nombre')}
</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{client.client_name || 'Sin nombre'}</h3>
                      <p className="text-sm text-gray-600">{client.client_email}</p>
                      {client.client_phone && (
                        <p className="text-sm text-gray-600">{client.client_phone}</p>
                      )}
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium 
                    ${getStatusColor(client.status)}`}>
                    {getStatusText(client.status)}
                  </span>
                </div>
                
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total visitas:</span>
                    <span className="font-medium">{client.total_appointments}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Completadas:</span>
                    <span className="font-medium text-green-600">{client.completed_appointments}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Última visita:</span>
                    <span className="font-medium">{formatDate(client.last_appointment)}</span>

                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total gastado:</span>
                    <span className="font-medium text-green-600">${client.total_spent}</span>
                  </div>
                  {client.favorite_service && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Servicio favorito:</span>
                      <span className="font-medium text-xs text-purple-600">{client.favorite_service}</span>
                    </div>
                  )}
                </div>

                {/* ✅ BOTONES ACTUALIZADOS - 3 columnas para salud, 2 para belleza */}
                <div className={`grid gap-2 ${isHealthProfessional ? 'grid-cols-3' : 'grid-cols-2'}`}>
                  <button 
                    onClick={() => handleViewDetail(client)}
                    className="bg-blue-100 text-blue-800 py-2 px-2 rounded text-sm hover:bg-blue-200 transition-colors"
                  >
                    Ver Detalle
                  </button>
                  
                  {/* ✅ NUEVO: Botón Historia Clínica (solo salud) */}
                  {isHealthProfessional && (
                    <button 
                      onClick={() => handleViewClinicalRecords(client)}
                      className="bg-green-100 text-green-800 py-2 px-2 rounded text-sm hover:bg-green-200 transition-colors"
                    >
                      H. Clínica
                    </button>
                  )}
                  
                  <button 
                    onClick={() => handleNewAppointment(client)}
                    className="bg-purple-100 text-purple-800 py-2 px-2 rounded text-sm hover:bg-purple-200 transition-colors"
                  >
                    Nueva Cita
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal de Detalle del Cliente */}
        {showDetailModal && selectedClient && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-16 h-16 bg-gradient-to-r ${theme.gradient} rounded-full flex items-center justify-center`}>
                      <span className="text-white text-xl font-bold">
                        {getInitials(selectedClient.client_name)}
                      </span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{selectedClient.client_name}</h2>
                      <p className="text-gray-600">{selectedClient.client_email}</p>
                      {selectedClient.client_phone && (
                        <p className="text-gray-600">{selectedClient.client_phone}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="p-6">
                {detailLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando historial...</p>
                  </div>
                ) : clientDetail ? (
                  <div>
                    {/* Resumen del cliente */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-2xl font-bold text-gray-900">{clientDetail.client.totalAppointments}</p>
                        <p className="text-sm text-gray-600">Total Citas</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{clientDetail.client.completedAppointments}</p>
                        <p className="text-sm text-gray-600">Completadas</p>
                      </div>
                      <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <p className="text-2xl font-bold text-purple-600">${clientDetail.client.totalSpent}</p>
                        <p className="text-sm text-gray-600">Total Gastado</p>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <p className="text-2xl font-bold text-blue-600">{clientDetail.client.favoriteServices.length}</p>
                        <p className="text-sm text-gray-600">Servicios</p>
                      </div>
                    </div>

                    {/* Servicios favoritos */}
                    {clientDetail.client.favoriteServices.length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Servicios Favoritos</h3>
                        <div className="flex flex-wrap gap-2">
                          {clientDetail.client.favoriteServices.map((service: string, index: number) => (
                            <span key={index} className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">
                              {service}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Historial de citas */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Historial de Citas</h3>
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {clientDetail.appointments.map((appointment: any) => (
                          <div key={appointment.appointment_id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium">{formatDate(appointment.appointment_date)}</span>
                                <span className="text-gray-600">{appointment.appointment_time}</span>
                                {appointment.is_first_time && (
                                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                    Primera vez
                                  </span>
                                )}
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status === 'completed' ? 'active' : 'inactive')}`}>
                                {appointment.status === 'completed' ? 'Completada' : appointment.status}
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-gray-600">Servicio:</span>
                                <p className="font-medium">{appointment.service_name || 'No especificado'}</p>
                              </div>
                              <div>
                                <span className="text-gray-600">Precio:</span>
                                <p className="font-medium">${appointment.price}</p>
                              </div>
                            </div>
                            
                            {appointment.notes && (
                              <div className="mt-2">
                                <span className="text-gray-600 text-sm">Notas:</span>
                                <p className="text-sm">{appointment.notes}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600">No se pudo cargar el detalle del cliente</p>
                  </div>
                )}
              </div>

              {/* ✅ FOOTER DEL MODAL ACTUALIZADO */}
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <div className={`grid gap-3 ${isHealthProfessional ? 'grid-cols-3' : 'grid-cols-2'}`}>
                  <button
                    onClick={() => handleNewAppointment(selectedClient)}
                    className={`bg-gradient-to-r ${theme.gradient} text-white py-2 px-4 rounded-lg hover:shadow-lg transition-shadow`}
                  >
                    Nueva Cita
                  </button>
                  
                  {/* ✅ NUEVO: Botón Historia Clínica en modal */}
                  {isHealthProfessional && (
                    <button
                      onClick={() => {
                        setShowDetailModal(false);
                        handleViewClinicalRecords(selectedClient);
                      }}
                      className="bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors"
                    >
                      Historia Clínica
                    </button>
                  )}
                  
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Clients;